import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FaCalendarAlt } from "react-icons/fa";
import Toast from "./Toast";
import "./Remainders.css";
import { API_BASE } from "../apiConfig";

const Remainders = () => {
  const [reminders, setReminders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selected, setSelected] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [announcement, setAnnouncement] = useState("");

  const feedbackTimer = useRef(null);
  const deleteButtonRef = useRef(null);
  const confirmPopupRef = useRef(null);
  const dateRef = useRef(null);

  // Show a toast/inline status message (auto-clears after 4 seconds)
  const showFeedback = (message, type = "success") => {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    setFeedback({ message, type });
    feedbackTimer.current = setTimeout(() => setFeedback(null), 4000);
  };

  // Form state
  const [formData, setFormData] = useState({
    Enquiry_date: "",
    Client_name: "",
    Client_ID: "",
    Reminder_Message: ""
  });

  // Helper to get token
  const getToken = () => localStorage.getItem("token");

  // Helper to check if a date belongs to a past month (not the current month)
  const isPastDate = (dateString) => {
    const today = new Date();
    const checkDate = new Date(dateString);

    // If the year is less than current year, it's a past month
    if (checkDate.getFullYear() < today.getFullYear()) {
      return true;
    }
    // If same year but month is less than current month, it's a past month
    if (checkDate.getFullYear() === today.getFullYear() && checkDate.getMonth() < today.getMonth()) {
      return true;
    }
    // Otherwise it's current month or future month
    return false;
  };

  // Fetch reminders for a given month (shared by the list and the date picker)
  const fetchRemindersForMonth = async (yearNum, monthNum) => {
    const token = getToken();
    if (!token) return null;

    const month = String(monthNum + 1).padStart(2, '0');
    const monthParam = `${yearNum}-${month}`;

    try {
      const response = await fetch(`${API_BASE}/therapist_api/get_reminder.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ month: monthParam })
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "success" && result.data) {
        let formattedData = [];
        const groupedData = result.data;

        Object.keys(groupedData).forEach(dateKey => {
          const itemsOnDate = groupedData[dateKey];

          itemsOnDate.forEach(item => {
            formattedData.push({
              ...item,
              date: item.reminder_date,
              name: item.client_name,
              message: item.reminder_message,
              color: "blue"
            });
          });
        });

        return formattedData;
      }
      return [];
    } catch (error) {
      console.error("Error fetching reminders:", error);
      return null;
    }
  };

  // Fetch Reminders based on current month
  useEffect(() => {
    fetchReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const fetchReminders = async () => {
    setIsLoading(true);
    const data = await fetchRemindersForMonth(currentDate.getFullYear(), currentDate.getMonth());
    if (data) setReminders(data);
    setIsLoading(false);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Create Reminder
  const handleSetReminder = async (e) => {
    e.preventDefault();
    const token = getToken();

    if (!formData.Enquiry_date || !formData.Client_name || !formData.Client_ID || !formData.Reminder_Message) {
      showFeedback("Please fill all the required fields", "error");
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/therapist_api/create_reminder.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: formData.Client_ID,
          client_name: formData.Client_name,
          reminder_date: formData.Enquiry_date,
          reminder_message: formData.Reminder_Message
        })
      });

      const result = await response.json();
      if (result.status === "success") {
        setFormData({
          Enquiry_date: "",
          Client_name: "",
          Client_ID: "",
          Reminder_Message: ""
        });
        fetchReminders();
        window.dispatchEvent(new Event("reminder-created"));
        showFeedback("Reminder created successfully", "success");
      } else {
        showFeedback(result.message || "Failed to create reminder", "error");
      }
    } catch (error) {
      console.error("Error creating reminder:", error);
      showFeedback("Error creating reminder. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to go to the previous month
  const handlePrevMonth = () => {
    const prev = new Date(currentDate);
    prev.setMonth(currentDate.getMonth() - 1);
    setCurrentDate(prev);
    setSelectAll(false);
    setSelected([]);
    setAnnouncement("No reminders selected");
  };

  // Function to go to the next month
  const handleNextMonth = () => {
    const next = new Date(currentDate);
    next.setMonth(currentDate.getMonth() + 1);
    setCurrentDate(next);
    setSelectAll(false);
    setSelected([]);
    setAnnouncement("No reminders selected");
  };

  // Format month and year nicely
  const monthName = currentDate.toLocaleString("default", { month: "long" });
  const year = currentDate.getFullYear();

  // 1. Select All gives you all the checkboxes to be selected
  const handleSelectAll = () => {
    if (selectAll) {
      // If already selecting, deselect all but KEEP checkboxes visible
      setSelectAll(false);
      setSelected([]);
      setAnnouncement("No reminders selected");
    } else {
      // Turn on select mode AND select all items automatically
      setSelectAll(true);
      const allIds = reminders.map((r) => r.id);
      setSelected(allIds);
      setAnnouncement(
        allIds.length === 0
          ? ""
          : `${allIds.length} ${allIds.length === 1 ? "reminder" : "reminders"} selected`
      );
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  };

  // 2. Click delete icon -> if items selected show confirmation popup,
  //    otherwise turn on select mode (shows checkboxes)
  const handleDelete = () => {
    if (selected.length > 0) {
      setShowDeleteConfirm(true);
    } else {
      if (!selectAll) {
        setSelectAll(true);
        setSelected([]); // Ensure nothing is selected
      }
      setAnnouncement("Select reminders to delete");
    }
  };

  // Close delete confirmation popup (No, Cancel) and restore focus
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    deleteButtonRef.current?.focus();
  };

  // Keep Tab focus inside the confirmation dialog; Escape closes it
  const handleConfirmKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      cancelDelete();
      return;
    }
    if (e.key === "Tab" && confirmPopupRef.current) {
      const focusables = confirmPopupRef.current.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // User clicked "Yes, Delete" - perform the actual deletion
  const confirmDeleteReminders = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    const token = getToken();

    try {
      const response = await fetch(`${API_BASE}/therapist_api/bulk_delete.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selected })
      });

      const result = await response.json();
      if (result.status === "success") {
        const deletedCount = selected.length;
        setReminders(reminders.filter(r => !selected.includes(r.id)));
        setSelected([]);
        setSelectAll(false);
        setShowDeleteConfirm(false);
        showFeedback(`${deletedCount} ${deletedCount === 1 ? "reminder" : "reminders"} deleted`, "success");
      } else {
        setShowDeleteConfirm(false);
        showFeedback("Failed to delete reminders", "error");
      }
    } catch (error) {
      console.error("Error deleting reminders:", error);
      setShowDeleteConfirm(false);
      showFeedback("Failed to delete reminders", "error");
    } finally {
      setIsDeleting(false);
      deleteButtonRef.current?.focus();
    }
  };

  const toggleSelect = (id) => {
    const newSelected = selected.includes(id)
      ? selected.filter((s) => s !== id)
      : [...selected, id];
    setSelected(newSelected);
    setAnnouncement(
      newSelected.length === 0
        ? "No reminders selected"
        : `${newSelected.length} ${newSelected.length === 1 ? "reminder" : "reminders"} selected`
    );
  };

  return (
    <div className="remainders-container">
      <span className="sr-only" role="status" aria-live="polite">{announcement}</span>

      {feedback && (
        <Toast
          type={feedback.type}
          message={feedback.message}
          onClose={() => setFeedback(null)}
          duration={0}
        />
      )}

      <div className="common-page-header">
        <h1 className="common-page-title reminders-title" style={{color:"#0063A5"}}>My Reminders</h1>
        <p className="common-page-subtitle">Set your reminders here for appointments and goals</p>
      </div>

      <div className="reminder-bar">
        <form className="reminder-form" onSubmit={handleSetReminder} aria-label="Create new reminder">
          <label htmlFor="Enquiry_date" className="sr-only">Reminder date</label>
          <div
            className="filter-date-box reminder-filter-date"
            role="button"
            tabIndex="0"
            onClick={() => dateRef.current?.showPicker()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                dateRef.current?.showPicker();
              }
            }}
            aria-label={`Reminder date${formData.Enquiry_date ? `: ${formData.Enquiry_date}` : ""}`}
            aria-haspopup="dialog"
          >
            <span className={!formData.Enquiry_date ? "filter-date-placeholder" : "filter-date-value"}>
              {formData.Enquiry_date || "Date"}
            </span>
            <FaCalendarAlt className="filter-date-icon" aria-hidden="true" />
            <input
              type="date"
              id="Enquiry_date"
              name="Enquiry_date"
              ref={dateRef}
              className="filter-date-input"
              value={formData.Enquiry_date}
              onChange={handleInputChange}
              min={new Date().toISOString().slice(0, 10)}
              aria-label="Reminder date"
            />
          </div>

          <label htmlFor="Client_name" className="sr-only">Client Details</label>
          <input
            type="text"
            id="Client_name"
            name="Client_name"
            placeholder="Client Name"
            className="reminder-input-Reminder1"
            value={formData.Client_name}
            onChange={handleInputChange}
            aria-label="Client Name"
          />

          <label htmlFor="Client_ID" className="sr-only">Client ID</label>
          <input
            type="text"
            id="Client_ID"
            name="Client_ID"
            placeholder="Client Details"
            className="reminder-input"
            value={formData.Client_ID}
            onChange={handleInputChange}
            aria-label="Client Details"
          />

          <label htmlFor="Reminder_Message" className="sr-only">Reminder Message</label>
          <input
            type="text"
            id="Reminder_Message"
            name="Reminder_Message"
            placeholder="Reminder Message"
            className="reminder-input-Reminder"
            value={formData.Reminder_Message}
            onChange={handleInputChange}
            aria-label="Reminder message"
          />

          <button type="submit" className="set-reminder-btn" aria-label="Set reminder" disabled={isSubmitting}>
            {isSubmitting ? "Setting..." : "Set Reminder"}
          </button>
        </form>
      </div>

      <div className="remainder-box">
        <div className="remainders-header-actions">
          <button
            className={`select-all-btn ${selectAll ? "selected" : ""}`}
            onClick={handleSelectAll}
          >
            {selectAll ? "Deselect All" : "Select All"}
          </button>

          <button ref={deleteButtonRef} onClick={handleDelete} aria-label={selected.length > 0 ? `Delete ${selected.length} selected reminders` : "Delete reminders"} title="Delete selected reminders" className="delect-btn1">
            <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
            <span className="delect-btn1-label">Delete Selected</span>
          </button>
        </div>

        <div className="remainders-calendar">
          <div className="calendar-month">
            <button className="arrow" onClick={handlePrevMonth} aria-label="Previous month">
              &lt;
            </button>
            <span className="month-label">
              {monthName} {year}
            </span>
            <button className="arrow" onClick={handleNextMonth} aria-label="Next month">
              &gt;
            </button>
          </div>

          <div className="remainders-list" role="list" aria-live="polite">
            {reminders.map((reminder) => (
              <div key={reminder.id} className="reminder-row" role="listitem">
                {/* Card */}
                <div
                  className={`reminder-card ${selected.includes(reminder.id) ? "selected" : ""} ${isPastDate(reminder.date) ? "past-month" : ""}`}
                >
                  <div className="reminder-content-wrapper">
                    <div className="reminder-top-row">
                      <div className="reminder-date">
                        <span
                          className="color-dot"
                          style={{ backgroundColor: reminder.color || 'blue' }}
                          aria-hidden="true"
                        ></span>
                        <span>{formatDate(reminder.date)}</span>
                      </div>

                      <div className="reminder-client-name">
                        <span>{reminder.name}</span>
                      </div>
                    </div>

                    <div className="reminder-client-details">
                      <span className="reminder-label">Client Details:</span>
                      <span className="reminder-value">{reminder.client_id}</span>
                    </div>

                    <div className="reminder-content">
                      <span className="reminder-label">Reminder Message:</span>
                      <p className="reminder-message">{reminder.message}</p>
                    </div>
                  </div>
                </div>

                {/* Checkbox OUTSIDE the card */}
                {selectAll && (
                  <div className="checkbox-outside">
                    <input
                      type="checkbox"
                      checked={selected.includes(reminder.id)}
                      onChange={() => toggleSelect(reminder.id)}
                      aria-label={`Select reminder for ${reminder.name}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          {isLoading && (
            <div className="remainders-status" role="status">Loading reminders...</div>
          )}
          {!isLoading && reminders.length === 0 && (
            <div className="remainders-status remainders-empty" role="status">
              No reminders for this month
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation popup */}
      {showDeleteConfirm && (
        <div
          className="reminder-confirm-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
          aria-describedby="delete-confirm-desc"
          onKeyDown={handleConfirmKeyDown}
          tabIndex={-1}
        >
          <div className="reminder-confirm-popup" ref={confirmPopupRef}>
            <h3 id="delete-confirm-title">Confirmation to Delete</h3>
            <p id="delete-confirm-desc">
              Are you sure, you want to delete {selected.length} {selected.length === 1 ? "reminder" : "reminders"}?
            </p>
            <div className="reminder-confirm-actions">
              <button
                className="confirm-yes-btn"
                onClick={confirmDeleteReminders}
                autoFocus
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                className="confirm-no-btn"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                No, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Remainders;
